import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { Card, Grid, Typography, CardContent } from "@mui/material";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const COLORS = [
  "#4f81bd", // ACE CRANE
  "#c0504d", // CNHi
  "#9bbb59", // CTPL
  "#8064a2", // ESCORT
  "#4bacc6", // GODREJ
  "#f79646", // KION
  "#2c4d75", // TAFE
  "#993366", // TMTL
  "#77933c", // Grand Total
];

const FloatInventoryStockDetails = ({ data, type = "count" }) => {
  const isEmpty =
    !data ||
    Object.keys(data).length === 0 ||
    Object.values(data).every((val) => Object.keys(val).length === 0);

  const { plantWiseData, overallData, oemList, plantList } = useMemo(() => {
    if (isEmpty)
      return { plantWiseData: [], overallData: {}, oemList: [], plantList: [] };

    const plantNames = Object.keys(data);
    const oemSet = new Set();

    const plantWiseData = plantNames.map((plant) => {
      const plantData = data[plant];
      Object.keys(plantData).forEach(
        (key) => key !== "grandTotal" && oemSet.add(key)
      );
      return { plant, ...plantData };
    });

    const oemList = Array.from(oemSet);

    const overallTotals = { grandTotal: 0 };
    oemList.forEach((oem) => {
      overallTotals[oem] = 0;
    });

    plantWiseData.forEach((row) => {
      oemList.forEach((oem) => {
        overallTotals[oem] += row[oem] || 0;
      });
      overallTotals.grandTotal += row.grandTotal || 0;
    });

    const overallData = { ...overallTotals };

    return { plantWiseData, overallData, oemList, plantList: plantNames };
  }, [data]);

  if (isEmpty) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        No data available
      </div>
    );
  }

  // Datasets for Plant Wise Chart
  const plantWiseDatasets = [...oemList, "grandTotal"].map((oem, index) => ({
    label: oem,
    backgroundColor: COLORS[index % COLORS.length],
    data: plantList.map((plant) => data[plant]?.[oem] || 0),
    barThickness: 30,
  }));

  const plantWiseChart = {
    labels: plantList,
    datasets: plantWiseDatasets,
  };

  const overallChart = {
    labels: [plantList.join(" & ")],
    datasets: [...oemList, "grandTotal"].map((oem, index) => ({
      label: oem,
      backgroundColor: COLORS[index % COLORS.length],
      data: [overallData[oem] || 0],
      barThickness: 30,
    })),
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: "bottom" },
      title: { display: false },
      datalabels:
        type === "cost"
          ? {
              display: false,
            }
          : {
              color: "#ffffff",
              anchor: "center",
              align: "center",
              font: {
                weight: "bold",
              },
            },
    },
  };

  return (
    <Grid container spacing={3}>
      <Grid item md={6}>
        <Typography sx={{ mb: 2 }}>
          Plant Wise - Float Engine {type === "cost" ? "Cost" : "Stock"} Details
        </Typography>
        <Bar data={plantWiseChart} options={chartOptions} />
      </Grid>
      <Grid item md={6}>
        <Typography sx={{ mb: 2 }}>
          Overall Float Engine {type === "cost" ? "Cost" : "Stock"} Details (ASP
          & SMP)
        </Typography>
        <Bar data={overallChart} options={chartOptions} />
      </Grid>
    </Grid>
  );
};

export default FloatInventoryStockDetails;
